import { toast } from "sonner";

import { WorkspacesApiError } from "../services/workspacesApi";

type WorkspaceErrorCopy = {
  title: string;
  description?: string;
};

const WORKSPACE_ERROR_COPY: Record<string, WorkspaceErrorCopy> = {
  "workspace.invalid_parent_child": {
    title: "Invalid connection",
    description: "That node type can't be added under the selected parent.",
  },
  "workspace.simulation_limit_reached": {
    title: "Simulation limit reached",
    description: "This report already has the maximum simulations for your plan. Delete one to run another.",
  },
  "workspace.duplicate_node": {
    title: "Already on canvas",
    description: "This entity is already linked on the workspace.",
  },
  "workspace.not_found": {
    title: "Workspace not found",
    description: "Open the workspace hub and try again.",
  },
  "workspace.name_conflict": {
    title: "Name already in use",
    description: "Choose a different workspace name for this account.",
  },
};

export function workspaceApiErrorCopy(slug: string | undefined): WorkspaceErrorCopy | null {
  if (!slug?.trim()) return null;
  return WORKSPACE_ERROR_COPY[slug.trim()] ?? null;
}

/** User-facing toast for workspace API failures (422 slugs and generic errors). */
export function toastWorkspaceApiError(err: unknown, fallbackTitle = "Workspace error"): void {
  if (err instanceof WorkspacesApiError) {
    const mapped = workspaceApiErrorCopy(err.slug);
    toast.error(mapped?.title ?? fallbackTitle, {
      description: mapped?.description ?? err.message,
    });
    return;
  }

  toast.error(fallbackTitle, {
    description: err instanceof Error ? err.message : "Something went wrong.",
  });
}

/** Toast when simulate-job save fails (incl. workspace.simulation_limit_reached). */
export function toastSimulateWorkspaceSaveFailure(
  saved: { detail?: string; slug?: string },
  fallbackTitle = "Simulation not saved"
): void {
  const mapped = workspaceApiErrorCopy(saved.slug);
  if (mapped) {
    toast.warning(mapped.title, {
      description: mapped.description,
    });
    return;
  }
  toast.warning(fallbackTitle, {
    description: saved.detail || "Could not save this run to the workspace.",
  });
}
